import type { InferOutput } from 'valibot';
import { optional as vOptional, object as vObject, safeParse as vSafeParse } from 'valibot'; // destruct to be sure that we don't ship whole valibot library to front
import { normalizeLanguage, type Language, languages, LanguageSchema } from '@@/shared/application';
import {
  type NormalizedParam,
  normalizeUrlParam,
  normalizeNumberSearchParam,
  normalizeArraySearchParam,
  normalizeStringSearchParam,
} from '@@/shared/url';
import { TRANSACTION_TYPE, FILTERABLE_FEATURES, PROJECT_TYPES_VALUES } from '@@/shared/property/types';
import type { TransactionType } from '@@/shared/property/types';
import { type EpcGesClass, EPC_GES_CLASS_VALUES } from '@@/shared/property/epc-ges';
import {
  TRANSACTION_TYPE_TRANSLATIONS, TRANSLATED_TRANSACTION_TYPES,
  FEATURE_TRANSLATIONS, TRANSLATED_FEATURES, PROPERTY_TYPE_TRANSLATIONS, TRANSLATED_PROPERTY_SEARCH_TYPES,
  PROJECT_TYPE_TRANSLATIONS, TRANSLATED_PROJECT_TYPES,
} from '@@/shared/property/url-referential';
import { NuxtRouteParamValueSchema, NuxtLocationQuerySchema } from '@@/shared/application';
import type { PropertySearchCriteria, PartialPropertySearchCriteria } from '@@/shared/property/search-criteria';
import { LocationRadiusSchema, createPropertySearchCriteria, SEARCH_PROPERTY_TYPES_VALUES } from '@@/shared/property/search-criteria';
import type { IadAppRoute } from '~/application';

// Thats all we can extract from page URL
export type SerpQueryParams = InferOutput<typeof SerpQuerySchema>;
export const SerpQuerySchema = vObject({
  // this will contain the url param slug
  serpSlug: vOptional(NuxtRouteParamValueSchema),

  // any additional query param that exists also in the slug
  location: vOptional(NuxtLocationQuerySchema),
  propertyType: vOptional(NuxtLocationQuerySchema),
  room: vOptional(NuxtLocationQuerySchema),
  feature: vOptional(NuxtLocationQuerySchema),

  // Those are only in query params
  radius: vOptional(NuxtLocationQuerySchema),
  priceMin: vOptional(NuxtLocationQuerySchema),
  priceMax: vOptional(NuxtLocationQuerySchema),
  surfaceMin: vOptional(NuxtLocationQuerySchema),
  surfaceMax: vOptional(NuxtLocationQuerySchema),
  epc: vOptional(NuxtLocationQuerySchema),
  reference: vOptional(NuxtLocationQuerySchema),
  bedroom: vOptional(NuxtLocationQuerySchema),
  groundSurfaceMin: vOptional(NuxtLocationQuerySchema),
  groundSurfaceMax: vOptional(NuxtLocationQuerySchema),
  projectType: vOptional(NuxtLocationQuerySchema),

  // pagination & sort
  page: vOptional(NuxtLocationQuerySchema),
  itemsPerPage: vOptional(NuxtLocationQuerySchema),
  sort: vOptional(NuxtLocationQuerySchema),

  // locale
  locale: vOptional(LanguageSchema),
});

type ParseSerpParams = {
  propertySearchCriteria: PropertySearchCriteria
  urlQueryParams: SerpQueryParams
  slug: NormalizedParam
};
// We will parse the slug in reverse order to get what is the most obvious first
// “Location” will be the one things that should remain at the end of slug analysis
// It's kind of a random string at first position
const parseFunctions: Array<(params: ParseSerpParams) => ParseSerpParams> = [
  getProjectTypes,
  getReference,
  getSorting,
  getSurfaces,
  getFeatures,
  getPrices,
  getRooms,
  getPropertyType,
  getTransactionType,
  getLocation,
  getEPC,
  getBedrooms,
  getGroundSurfaces,
];

/**
 * Transform a URL query params to a PropertySearchCriteria
 *
 * “PropertySearchCriteria” are the normalized way to query the API (un-translated, with numbers…)
 *
 * “serpSlug” should be included in the query params
 *
 * It's what is required by the API
 */
export function getCriteriaFromQuery(urlQueryParams: SerpQueryParams): PropertySearchCriteria {
  const slug = normalizeUrlParam(urlQueryParams.serpSlug);
  const propertySearchCriteria: PropertySearchCriteria = {
    page: normalizeNumberSearchParam(urlQueryParams.page) ?? 1,
    itemsPerPage: normalizeNumberSearchParam(urlQueryParams.itemsPerPage) ?? 30,
    features: [],
    locations: [],
    propertyTypes: [],
    rooms: [],
    epc: [],
    bedrooms: [],
    projectTypes: [],
    sort: `dateDesc`,
  };
  const parseSerpSlugParams: ParseSerpParams = {
    propertySearchCriteria,
    slug,
    urlQueryParams,
  };
  const result = parseFunctions.reduce((acc, fn) => fn(acc), parseSerpSlugParams);
  return result.propertySearchCriteria;
}

function getProjectTypes({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  // slug analysis
  const slugItemIndex = slug.findIndex(slugParam => slugParam in TRANSLATED_PROJECT_TYPES);
  if (slugItemIndex > -1) {
    const projectTypeFromSlug = slug[slugItemIndex] ? TRANSLATED_PROJECT_TYPES[slug[slugItemIndex]] : ``;
    if (projectTypeFromSlug) propertySearchCriteria.projectTypes.push(projectTypeFromSlug);
    slug = slug.toSpliced(slugItemIndex, 1);
  }
  // query analysis
  const queryProjectTypes = normalizeArraySearchParam(urlQueryParams.projectType);
  if (!queryProjectTypes.length) return { propertySearchCriteria, slug, urlQueryParams };
  const supportedProjectTypes = queryProjectTypes
    .map(queryProjectType => TRANSLATED_PROJECT_TYPES[queryProjectType])
    .filter(queryProjectType => queryProjectType != null);
  propertySearchCriteria.projectTypes = [...new Set(propertySearchCriteria.projectTypes.concat(supportedProjectTypes))];
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getReference({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  const reference = normalizeStringSearchParam(urlQueryParams.reference);
  if (reference) propertySearchCriteria.propertyListingRef = reference;
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getSorting({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  if (urlQueryParams.sort === `priceAsc`) propertySearchCriteria.sort = `priceAsc`;
  else if (urlQueryParams.sort === `priceDesc`) propertySearchCriteria.sort = `priceDesc`;
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getSurfaces({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  propertySearchCriteria.surfaceMin = normalizeNumberSearchParam(urlQueryParams.surfaceMin);
  propertySearchCriteria.surfaceMax = normalizeNumberSearchParam(urlQueryParams.surfaceMax);
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getGroundSurfaces({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  propertySearchCriteria.groundSurfaceMin = normalizeNumberSearchParam(urlQueryParams.groundSurfaceMin);
  propertySearchCriteria.groundSurfaceMax = normalizeNumberSearchParam(urlQueryParams.groundSurfaceMax);
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getPrices({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  propertySearchCriteria.priceMin = normalizeNumberSearchParam(urlQueryParams.priceMin);
  propertySearchCriteria.priceMax = normalizeNumberSearchParam(urlQueryParams.priceMax);
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getFeatures({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  // slug analysis
  const slugItemIndex = slug.findIndex(slugParam => slugParam in TRANSLATED_FEATURES);
  if (slugItemIndex > -1) {
    const featureFromSlug = slug[slugItemIndex] ? TRANSLATED_FEATURES[slug[slugItemIndex]] : ``;
    if (featureFromSlug) propertySearchCriteria.features.push(featureFromSlug);
    slug = slug.toSpliced(slugItemIndex, 1);
  }
  // query analysis
  const queryFeatures = normalizeArraySearchParam(urlQueryParams.feature);
  if (!queryFeatures.length) return { propertySearchCriteria, slug, urlQueryParams };
  const supportedFeatures = normalizeArraySearchParam(urlQueryParams.feature)
    .map(queryPropertyType => TRANSLATED_FEATURES[queryPropertyType])
    .filter(queryPropertyType => queryPropertyType != null);
  propertySearchCriteria.features = [...new Set(propertySearchCriteria.features.concat(supportedFeatures))];
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getEPC({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  const queryEPCClasses = normalizeArraySearchParam(urlQueryParams.epc);
  const supportedDPEClasses = queryEPCClasses.filter(queryEPCClass => EPC_GES_CLASS_VALUES.includes(queryEPCClass as EpcGesClass)) as Array<EpcGesClass>;
  propertySearchCriteria.epc = [...new Set(propertySearchCriteria.epc.concat(supportedDPEClasses))];
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getRooms({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  // slug analysis
  const slugItemIndex = slug.findIndex(item => /^t(?<room>[1-6])$/.test(item));
  if (slugItemIndex > -1) {
    const item = slug[slugItemIndex] ?? ``;
    const urlRoom = /^t(?<room>[1-6])$/.exec(item);
    if (urlRoom?.groups?.room) propertySearchCriteria.rooms.push(Number(urlRoom.groups.room));
    slug = slug.toSpliced(slugItemIndex, 1);
  }
  // query analysis
  const queryRooms = normalizeArraySearchParam(urlQueryParams.room)
    .filter(room => typeof room === 'string' && /^[1-6]$/.test(room))
    .map(room => Number(room));
  const roomsWithBoundaries = propertySearchCriteria.rooms
    .concat(queryRooms)
    .map(room => Math.max(0, Math.min(6, room)));
  propertySearchCriteria.rooms = [...new Set(roomsWithBoundaries)];
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getBedrooms({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  const queryBedrooms = normalizeArraySearchParam(urlQueryParams.bedroom)
    .filter(bedroom => typeof bedroom === 'string' && /^[1-6]$/.test(bedroom))
    .map(bedroom => Number(bedroom));
  const bedroomsWithBoundaries = propertySearchCriteria.bedrooms
    .concat(queryBedrooms)
    .map(room => Math.max(0, Math.min(5, room)));
  propertySearchCriteria.bedrooms = [...new Set(bedroomsWithBoundaries)];
  return { propertySearchCriteria, slug, urlQueryParams };
}

function getPropertyType({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  // slug analysis
  const slugItemIndex = slug.findIndex(slugParam => slugParam in TRANSLATED_PROPERTY_SEARCH_TYPES);
  if (slugItemIndex > -1) {
    const propertyTypeFromSlug = slug[slugItemIndex] ? TRANSLATED_PROPERTY_SEARCH_TYPES[slug[slugItemIndex]] : ``;
    if (propertyTypeFromSlug) propertySearchCriteria.propertyTypes.push(propertyTypeFromSlug);
    slug = slug.toSpliced(slugItemIndex, 1);
  }
  // query analysis
  const queryPropertyTypes = normalizeArraySearchParam(urlQueryParams.propertyType);
  const supportedPropertyTypes = queryPropertyTypes
    .map(queryPropertyType => TRANSLATED_PROPERTY_SEARCH_TYPES[queryPropertyType])
    .filter(queryPropertyType => queryPropertyType != null);
  propertySearchCriteria.propertyTypes = [...new Set(propertySearchCriteria.propertyTypes.concat(supportedPropertyTypes))];

  return { propertySearchCriteria, slug, urlQueryParams };
}

function getTransactionType({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  const slugItemIndex = slug.findIndex(slugParam => slugParam in TRANSLATED_TRANSACTION_TYPES);
  if (slugItemIndex === -1) return { propertySearchCriteria, slug, urlQueryParams };
  propertySearchCriteria.transactionType = TRANSLATED_TRANSACTION_TYPES[slug[slugItemIndex] as string];
  return { propertySearchCriteria, slug: slug.toSpliced(slugItemIndex, 1), urlQueryParams };
}

function getLocation({ slug, propertySearchCriteria, urlQueryParams }: ParseSerpParams): ParseSerpParams {
  // TODO: secure more with a regex
  // • ile-de-france (region)
  // • seine-et-marne-77 (département)
  // • perpignan-66000 (ville)
  // • pg-meudon_92 (groupe de communes)
  // slug analysis
  if (slug[0]) propertySearchCriteria.locations = [slug[0]];

  // query analysis
  const locations = normalizeArraySearchParam(urlQueryParams.location);
  if (locations) {
    const uniqueLocations = [...new Set(propertySearchCriteria.locations.concat(locations))];
    propertySearchCriteria.locations = uniqueLocations;
  }
  const radius = vSafeParse(LocationRadiusSchema, normalizeStringSearchParam(urlQueryParams.radius));
  if (radius.success) propertySearchCriteria.radius = radius.output;
  return { propertySearchCriteria, slug: [], urlQueryParams };
}

interface NormalizedSerpQueryParams {
  serpSlug: Array<string>
  query: Omit<SerpQueryParams, 'serpSlug'>
}

/**
 * Transform DB Search criteria to URL params
 *
 * This will also translate any normalized value to the desired locale (used to handle URL i18n)
 *
 * It's what is required by the Vue Router to persist a user search
 */
export function getUrlParamsFromCriteria(lang: Language, partialPropertySearchCriteria: PartialPropertySearchCriteria): NormalizedSerpQueryParams {
  const propertySearchCriteria = createPropertySearchCriteria(partialPropertySearchCriteria);
  const [mainLocation, ...otherLocations] = propertySearchCriteria.locations;
  const [mainPropertyType, ...otherPropertyTypes] = reorderParams(propertySearchCriteria.propertyTypes, SEARCH_PROPERTY_TYPES_VALUES);
  const fourthLevelData = handleFourthLevelSlug(lang, propertySearchCriteria);
  const translatedSlug = [
    mainLocation,
    propertySearchCriteria.transactionType
      ? TRANSACTION_TYPE_TRANSLATIONS[propertySearchCriteria.transactionType][lang]
      : undefined,
    mainPropertyType
      ? PROPERTY_TYPE_TRANSLATIONS[mainPropertyType][lang]
      : undefined,
    fourthLevelData.slug,
  ];
  const serpSlug = translatedSlug.filter(value => typeof value === 'string');
  return {
    serpSlug,
    query: {
      location: otherLocations.length
        ? otherLocations
        : undefined,
      radius: propertySearchCriteria.radius,
      propertyType: otherPropertyTypes.length
        ? otherPropertyTypes.map(propertyType => PROPERTY_TYPE_TRANSLATIONS[propertyType][lang])
        : undefined,
      ...fourthLevelData.query,
      epc: propertySearchCriteria.epc.length
        ? propertySearchCriteria.epc
        : undefined,
      bedroom: propertySearchCriteria.bedrooms.length
        ? propertySearchCriteria.bedrooms.map(String)
        : undefined,
      sort: propertySearchCriteria.sort !== `dateDesc` ? propertySearchCriteria.sort : undefined,
      reference: propertySearchCriteria.propertyListingRef,
      // We do want to filter “0” values
      priceMin: propertySearchCriteria.priceMin ? String(propertySearchCriteria.priceMin) : undefined,
      priceMax: propertySearchCriteria.priceMax ? String(propertySearchCriteria.priceMax) : undefined,
      surfaceMin: propertySearchCriteria.surfaceMin ? String(propertySearchCriteria.surfaceMin) : undefined,
      surfaceMax: propertySearchCriteria.surfaceMax ? String(propertySearchCriteria.surfaceMax) : undefined,
      groundSurfaceMin: propertySearchCriteria.groundSurfaceMin ? String(propertySearchCriteria.groundSurfaceMin) : undefined,
      groundSurfaceMax: propertySearchCriteria.groundSurfaceMax ? String(propertySearchCriteria.groundSurfaceMax) : undefined,
    },
  };
}
interface FourthLevel {
  slug?: string
  query: Pick<NormalizedSerpQueryParams['query'], 'room' | 'projectType' | 'feature'>
}
function handleFourthLevelSlug(lang: Language, propertySearchCriteria: PropertySearchCriteria): FourthLevel {
  const orderedProjectTypes = reorderParams(propertySearchCriteria.projectTypes, PROJECT_TYPES_VALUES)
    .map(projectType => PROJECT_TYPE_TRANSLATIONS[projectType][lang]);
  const orderedFeatures = reorderParams(propertySearchCriteria.features, FILTERABLE_FEATURES)
    .map(feature => FEATURE_TRANSLATIONS[feature][lang]);
  if (propertySearchCriteria.rooms.length) {
    const room = propertySearchCriteria.rooms.slice(1).map(String);
    return {
      slug: `t${propertySearchCriteria.rooms[0]}`,
      query: {
        room: room.length ? room : undefined,
        projectType: orderedProjectTypes.length ? orderedProjectTypes : undefined,
        feature: orderedFeatures.length ? orderedFeatures : undefined,
      },
    };
  }
  if (propertySearchCriteria.projectTypes.length) {
    const projectType = orderedProjectTypes.slice(1);
    return {
      slug: orderedProjectTypes[0],
      query: {
        projectType: projectType.length ? projectType : undefined,
        feature: orderedFeatures.length ? orderedFeatures : undefined,
      },
    };
  }
  const feature = orderedFeatures.slice(1);
  return {
    slug: orderedFeatures[0],
    query: {
      feature: feature.length ? feature : undefined,
    },
  };
}
function reorderParams<T>(toReorder: Array<T>, source: ReadonlyArray<T>): Array<T> {
  return source.filter(value => toReorder.includes(value));
}

// Used for generating <head> alternate urls
function translateSerpSlug(lang: Language, serpSlug?: string | string[]): NormalizedParam {
  const slug = normalizeUrlParam(serpSlug);
  const propertySearchCriteria = getCriteriaFromQuery({ serpSlug: slug });
  return getUrlParamsFromCriteria(lang, propertySearchCriteria).serpSlug;
}

export function getTranslatedSerpSlug(serpSlug?: string | string[]): Record<Language, NormalizedParam> {
  const result = languages.map(lang => [lang, translateSerpSlug(lang, serpSlug)]);
  return Object.fromEntries(result);
}

type SerpRoute = IadAppRoute<'serp-serpSlug'> & {
  params: {
    serpSlug: NonNullable<IadAppRoute<'serp-serpSlug'>['params']['serpSlug']>
  }
};

export function getMainTranslatedSerpTo(locale: string): Record<TransactionType, SerpRoute> {
  const lang = normalizeLanguage(locale);
  return {
    [TRANSACTION_TYPE.SALE]: {
      name: `serp-serpSlug`,
      params: { serpSlug: translateSerpSlug(lang, [TRANSACTION_TYPE.SALE]) },
    },
    [TRANSACTION_TYPE.RENTAL]: {
      name: `serp-serpSlug`,
      params: { serpSlug: translateSerpSlug(lang, [TRANSACTION_TYPE.RENTAL]) },
    },
  };
}
